import {
    BackButton,
    NextButton,
    SaveButton,
    SendButton,
    SkipButton,
    SubmitButton,
} from "@/features/i18n/formComponents";
import { Button, ButtonLabel, type ButtonProps } from "@patient-watch/ui/button";

import { cn } from "../../lib/utils";

type ButtonTypeT = "submit" | "button";

export type FooterPrimaryAction = "next" | "submit" | "save" | "send";
export type FooterSecondaryAction = "back" | "skip";
export type FooterTertiaryAction = "skip";

interface Props {
    func1?: (event?: React.MouseEvent<HTMLButtonElement>) => void;
    func2?: () => void;
    func3?: () => void;
    words1: string;
    icon1?: React.ReactNode;
    /** Primary control style. Defaults to the solid indigo button. */
    variant1?: ButtonProps["variant"];
    words2?: string;
    icon2?: React.ReactNode;
    words3?: string;
    icon3?: React.ReactNode;
    /** Third control style. Defaults to secondary. */
    variant3?: "secondary" | "destructive-secondary";
    primaryAction?: FooterPrimaryAction;
    secondaryAction?: FooterSecondaryAction;
    /** Third control under the pair. `skip` uses the shared skip button. */
    tertiaryAction?: FooterTertiaryAction;
    disabled1?: boolean;
    disabled2?: boolean;
    disabled3?: boolean;
    isLoading1?: boolean;
    isLoading2?: boolean;
    isLoading3?: boolean;
    type1?: ButtonTypeT;
    type2?: ButtonTypeT;
    type3?: ButtonTypeT;
    /** Primary control test id. Defaults to `footer-primary-button`. */
    testId1?: string;
}

/** Allow long dynamic labels (e.g. "Add Pain And Comments Before Injection Entry") to wrap. */
const footerButtonClassName = "h-auto min-h-14 w-full whitespace-normal text-balance px-4 py-3 leading-snug";

const footerSecondaryButtonClassName = "h-auto min-h-14 w-full whitespace-nowrap px-4 py-3 leading-snug";

const footerTertiaryButtonClassName = "h-auto min-h-14 w-full whitespace-normal text-balance px-4 py-3 leading-snug";

const sharedPrimaryProps = (props: Props) => ({
    className: footerButtonClassName,
    disabled: props.disabled1,
    isLoading: props.isLoading1,
    "data-testid": props.testId1 ?? "footer-primary-button",
});

const sharedSecondaryProps = (props: Props) => ({
    className: footerSecondaryButtonClassName,
    disabled: props.disabled2,
    isLoading: props.isLoading2,
    "data-testid": "footer-secondary-button" as const,
});

function renderPrimaryButton(props: Props) {
    const { func1, words1, primaryAction, type1 } = props;
    const shared = sharedPrimaryProps(props);

    switch (primaryAction) {
        case "next":
            return <NextButton {...shared} label={words1} onClick={func1} />;
        case "submit":
            return <SubmitButton {...shared} label={words1} onClick={func1} />;
        case "save":
            return <SaveButton {...shared} label={words1} onClick={func1} />;
        case "send":
            return <SendButton {...shared} label={words1} onClick={func1} />;
        default:
            return (
                <Button {...shared} size="lg" variant={props.variant1} onClick={func1} type={type1 ?? "submit"}>
                    <ButtonLabel icon={props.icon1}>{words1}</ButtonLabel>
                </Button>
            );
    }
}

function renderSecondaryButton(props: Props) {
    const { func2, words2, secondaryAction, type2 } = props;
    const shared = sharedSecondaryProps(props);

    if (!func2 || !words2) return null;

    switch (secondaryAction) {
        case "back":
            return <BackButton {...shared} label={words2} onClick={func2} />;
        case "skip":
            return <SkipButton {...shared} label={words2} onClick={func2} />;
        default:
            return (
                <Button
                    {...shared}
                    size="lg"
                    onClick={func2}
                    variant="secondary"
                    type={type2 ?? "button"}
                    className={cn(
                        footerSecondaryButtonClassName,
                        props.icon2 && "flex justify-center items-center gap-2"
                    )}
                >
                    <ButtonLabel icon={props.icon2}>{words2}</ButtonLabel>
                </Button>
            );
    }
}

function renderTertiaryButton(props: Props) {
    const { func3, words3, tertiaryAction, type3 } = props;
    if (!words3) return null;

    if (tertiaryAction === "skip") {
        return (
            <SkipButton
                className={footerTertiaryButtonClassName}
                label={words3}
                onClick={func3}
                disabled={props.disabled3}
                isLoading={props.isLoading3}
            />
        );
    }

    return (
        <Button
            className={cn(footerTertiaryButtonClassName, props.icon3 && "flex justify-center items-center gap-2")}
            size="lg"
            onClick={func3}
            variant={props.variant3 ?? "secondary"}
            type={type3 ?? "button"}
            disabled={props.disabled3}
            data-testid="footer-tertiary-button"
        >
            <ButtonLabel icon={props.icon3}>{words3}</ButtonLabel>
        </Button>
    );
}

const FooterButtons = (props: Props) => {
    const { func2, words2 } = props;
    const hasSecondary = Boolean(func2 && words2);
    const tertiary = renderTertiaryButton(props);

    return (
        <div className="footer-actions-frame">
            <div className="footer-actions">
                <div className="footer-actions-primary">{renderPrimaryButton(props)}</div>
                {hasSecondary ? <div className="footer-actions-secondary">{renderSecondaryButton(props)}</div> : null}
            </div>
            {tertiary ? <div className="footer-actions-tertiary">{tertiary}</div> : null}
        </div>
    );
};

export default FooterButtons;
